import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { formatTzPhoneDisplay, isValidIntlPhone } from '../booking/phone';
import IntlPhoneField from '../components/IntlPhoneField';

const fieldClass =
  'w-full rounded-lg border border-gray-200 bg-white px-3.5 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-superfeo-pink focus:outline-none focus:ring-2 focus:ring-superfeo-pink/20 transition';

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="mb-1.5 flex items-baseline gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
        <span>{label}</span>
        {required ? <span className="text-superfeo-pink">*</span> : null}
      </label>
      {children}
      {hint ? <p className="mt-1 text-xs text-gray-400">{hint}</p> : null}
    </div>
  );
}

export default function AccountPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login | register
  const [identifier, setIdentifier] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ageGroup, setAgeGroup] = useState('adult');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (mode === 'register') {
      if (!name.trim() || name.trim().length < 2) {
        setError('Full name is required.');
        return;
      }
      if (ageGroup !== 'adult' && ageGroup !== 'child') {
        setError('Select age group: adult or child.');
        return;
      }
      if (!isValidIntlPhone(phone)) {
        setError('Enter a valid phone number with country code.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setError('Enter a valid email address.');
        return;
      }
      if (!password || password.length < 6) {
        setError('Password must be at least 6 characters.');
        return;
      }
    } else {
      if (!identifier.trim()) {
        setError('Enter your phone number or email.');
        return;
      }
      if (!password) {
        setError('Password is required.');
        return;
      }
    }

    try {
      if (mode === 'register') {
        const data = await auth.register({
          phone,
          password,
          name: name.trim(),
          email: email.trim(),
          ageGroup,
        });
        if (data?.emailSent) {
          setInfo('Account created. A welcome email was sent to you.');
        } else {
          setInfo('Account created. Welcome email could not be sent yet — you can still use the app.');
        }
      } else {
        await auth.login({ identifier: identifier.trim(), password });
        setInfo('Welcome back. Tickets synced.');
      }
      navigate('/tickets');
    } catch (err) {
      setError(err.message || 'Could not continue.');
    }
  };

  if (auth.isLoggedIn) {
    return (
      <div className="min-h-[70vh] bg-[linear-gradient(160deg,#fff5f8_0%,#ffffff_45%,#f8fafc_100%)] py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_12px_40px_rgba(17,24,39,0.06)] overflow-hidden">
            <div className="bg-gray-900 px-6 py-5 text-white">
              <p className="text-[11px] tracking-[0.16em] uppercase text-white/60">Superfeo Express</p>
              <h1 className="text-2xl font-heading font-bold mt-1">Your account</h1>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Passenger</p>
                <p className="text-lg font-semibold text-gray-900">
                  {auth.user?.name || 'Traveller'}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="rounded-xl bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-400">Mobile</p>
                  <p className="font-medium text-gray-900">
                    {formatTzPhoneDisplay(auth.user?.phone)}
                  </p>
                </div>
                {auth.user?.email ? (
                  <div className="rounded-xl bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-400">Email</p>
                    <p className="font-medium text-gray-900 break-all">{auth.user.email}</p>
                  </div>
                ) : null}
                {auth.user?.ageGroup ? (
                  <div className="rounded-xl bg-gray-50 px-4 py-3">
                    <p className="text-xs text-gray-400">Age group</p>
                    <p className="font-medium text-gray-900 capitalize">{auth.user.ageGroup}</p>
                  </div>
                ) : null}
              </div>
              <Link
                to="/tickets"
                className="block text-center bg-superfeo-pink hover:bg-superfeo-darkpink text-white font-bold py-3.5 rounded-xl transition"
              >
                My tickets
              </Link>
              <button
                type="button"
                onClick={() => auth.logout()}
                className="w-full text-sm font-semibold text-gray-600 py-2 hover:text-gray-900"
              >
                Sign out
              </button>
              <p className="text-xs text-gray-400 text-center pt-2">
                Support:{' '}
                <a className="text-superfeo-pink font-medium" href="mailto:info@superfeoexpress.co.tz">
                  info@superfeoexpress.co.tz
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] bg-[linear-gradient(160deg,#fff5f8_0%,#ffffff_45%,#f8fafc_100%)] py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_12px_40px_rgba(17,24,39,0.06)] overflow-hidden">
          <div className="bg-gray-900 px-6 py-6 text-white">
            <p className="text-[11px] tracking-[0.16em] uppercase text-white/60">Superfeo Express</p>
            <h1 className="text-2xl font-heading font-bold mt-1">
              {mode === 'login' ? 'Sign in' : 'Create your account'}
            </h1>
            <p className="text-sm text-white/70 mt-2 leading-relaxed">
              {mode === 'login'
                ? 'Access saved tickets and continue booking with your Superfeo profile.'
                : 'Secure your ticket history across devices. All fields are required.'}
            </p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100 mb-6">
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError('');
                  setInfo('');
                }}
                className={`py-2.5 rounded-lg text-sm font-bold transition ${
                  mode === 'login'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('register');
                  setError('');
                  setInfo('');
                }}
                className={`py-2.5 rounded-lg text-sm font-bold transition ${
                  mode === 'register'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                Register
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4" noValidate>
              {mode === 'register' ? (
                <>
                  <Field label="Full name" required>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={fieldClass}
                      placeholder="Your full name"
                      autoComplete="name"
                      required
                    />
                  </Field>
                  <Field label="Age group" required>
                    <div className="grid grid-cols-2 gap-2">
                      {['adult', 'child'].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setAgeGroup(g)}
                          className={`py-3 rounded-lg text-sm font-bold border transition capitalize ${
                            ageGroup === g
                              ? 'bg-superfeo-pink text-white border-superfeo-pink'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-superfeo-pink/40'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Mobile number" required hint="Select country code, then enter your number">
                    <IntlPhoneField
                      value={phone}
                      onChangeE164={setPhone}
                      autoComplete="tel"
                      required
                    />
                  </Field>
                  <Field label="Email" required hint="Welcome email from info@superfeoexpress.co.tz">
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={fieldClass}
                      placeholder="you@example.com"
                      type="email"
                      autoComplete="email"
                      required
                    />
                  </Field>
                </>
              ) : (
                <Field label="Phone or email" required>
                  <input
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className={fieldClass}
                    placeholder="07XXXXXXXX or email@domain.com"
                    autoComplete="username"
                    required
                  />
                </Field>
              )}

              <Field label="Password" required>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={fieldClass}
                  placeholder={mode === 'login' ? 'Your password' : 'At least 6 characters'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  required
                  minLength={mode === 'register' ? 6 : undefined}
                />
              </Field>

              {error && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                  {error}
                </p>
              )}
              {info && (
                <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5">
                  {info}
                </p>
              )}

              <button
                type="submit"
                disabled={auth.busy}
                className="w-full bg-superfeo-pink hover:bg-superfeo-darkpink disabled:bg-gray-300 text-white font-bold py-3.5 rounded-xl transition"
              >
                {auth.busy
                  ? 'Please wait…'
                  : mode === 'login'
                    ? 'Sign in'
                    : 'Create account'}
              </button>
            </form>

            <p className="text-center mt-6 text-xs text-gray-400 leading-relaxed">
              Accounts are optional for booking. Ticket history stays on this device either way.
              <br />
              <Link to="/tickets" className="text-superfeo-pink font-semibold">
                View tickets on this device →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
